/**
 * Insight Cards — the curated reports Priori prepared for this club, at the
 * top of /insights.
 *
 * FAILS TO NOTHING, like InsightsFeatured and InsightsReports beneath it: an
 * absent or refused endpoint renders no section rather than an error, so a
 * club with no cards — which is every club but the one in the registry — sees
 * the page exactly as it was.
 *
 * THE COUNT IN THE HEADING IS DERIVED, never written down. One card today;
 * the same heading reads "Your Insights (6)" the day there are six, and the
 * grid already lays them out. When there are enough to want "Opposition
 * Scouting" and "Trend Analysis" headings, `category` is what they group by —
 * it is the card's kicker today, so one value drives both.
 *
 * THE THEME RIDES IN THE URL. A report is served sandboxed into an opaque
 * origin, where its own `localStorage.getItem('priori-theme')` throws and it
 * falls back to the OS — so someone reading the product in light on a dark
 * laptop would open a dark report. `?theme=` is read first by the imported
 * report (scripts/import_insight_card.py seeds that), and the shell passes it
 * on to its sub-documents through the postMessage it already had.
 */
import { useEffect, useState } from 'react'

import { insightCardViewUrl, listInsightCards, type InsightCard } from '../packs/promotions'
import { useCurrentTheme } from '../theme/useCurrentTheme'

import './insights.css'

/** '2026-10-14' -> 'October 14, 2026'.
 *
 *  Split and rebuilt rather than `new Date('2026-10-14')`, which parses as
 *  UTC midnight and renders as the 13th for anyone behind it. */
const readableDate = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return iso
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
  })
}

/* These are real links, so the href has to be correct before the click, not
   computed during it: cmd-click, middle-click and "open in new tab" never
   run an onClick handler. useCurrentTheme (shared with the pack viewer)
   watches <html data-theme>, so someone who toggles the theme in the
   sidebar and then opens a card gets the theme they are looking at. */

export default function InsightsCards({ onCount }: {
  /** How many cards rendered (0 on empty or failure). The parent's
   *  empty-feed copy must not claim the page is empty while this section
   *  is showing a card. */
  onCount?: (n: number) => void
} = {}) {
  const [cards, setCards] = useState<InsightCard[] | null>(null)
  const theme = useCurrentTheme()

  useEffect(() => {
    listInsightCards()
      .then((d) => { setCards(d.cards); onCount?.(d.cards.length) })
      .catch(() => { setCards(null); onCount?.(0) })
    // onCount is a setState from the parent; identity churn must not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!cards || cards.length === 0) return null

  return (
    <section className="ins-cards">
      <h2 className="ins-cards__title">
        Your Insights <span className="ins-cards__count">({cards.length})</span>
      </h2>
      <div className="ins-grid">
        {cards.map((c) => (
          <a key={c.slug} className="ins-card-link"
             href={insightCardViewUrl(c.slug, theme)}
             target="_blank" rel="noopener">
            {c.category && <span className="ins-card-link__kicker">{c.category}</span>}
            <strong className="ins-card-link__name">{c.title}</strong>
            <span className="ins-card-link__foot">
              <span className="ins-card-link__when">{readableDate(c.date)}</span>
              {/* aria-hidden: "Open" after the title is noise to a screen
                  reader, which has already been told this is a link. */}
              <span className="ins-card-link__go" aria-hidden="true">
                Open <span>→</span>
              </span>
            </span>
          </a>
        ))}
      </div>
    </section>
  )
}
